import { Capacitor } from '@capacitor/core';
import { ensureAdminAlertsChannel } from '@/lib/pushChannel';

/**
 * App-boot FCM priming.
 *
 * Why: `useFcmRegistration` only mounts AFTER a successful login (it gates on
 * `driver?.id`). On a brand-new install, the first `verifyOtp` therefore runs
 * BEFORE Firebase Installations has ever been initialised — so the inline
 * `FCM.refreshToken()` call returns null, the server writes no `push_tokens`
 * row, and the first-login session receives no FCM notifications.
 *
 * This module initialises FCM at app boot (independent of auth) so that by
 * the time the user submits OTP, a real registration token is already cached
 * and can be passed straight to `driver-login.verify_otp`.
 *
 * Android-native only. No-op on iOS / web.
 */

let primedToken: string | null = null;
let bootstrapStarted = false;
const waiters: Array<(token: string | null) => void> = [];

function resolveWaiters(token: string | null) {
  while (waiters.length) {
    const w = waiters.shift();
    try { w?.(token); } catch { /* ignore */ }
  }
}

export function getPrimedFcmToken(): string | null {
  return primedToken;
}

export function clearPrimedFcmToken(): void {
  primedToken = null;
}

/**
 * Resolve with the primed FCM token, waiting up to `timeoutMs` for the
 * `registration` callback to fire if it hasn't already. Resolves to null on
 * non-Android, on timeout, or on registrationError.
 */
export function waitForFcmToken(timeoutMs = 6000): Promise<string | null> {
  if (primedToken) return Promise.resolve(primedToken);
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') {
    return Promise.resolve(null);
  }
  return new Promise((resolve) => {
    let done = false;
    const finish = (token: string | null) => {
      if (done) return;
      done = true;
      resolve(token);
    };
    waiters.push(finish);
    setTimeout(() => finish(null), timeoutMs);
  });
}

/**
 * Idempotent — safe to call multiple times. Runs at most once per process.
 * Fire-and-forget; never throws.
 */
export function primeFcmAtBoot(): void {
  if (bootstrapStarted) return;
  bootstrapStarted = true;
  if (!Capacitor.isNativePlatform()) return;
  if (Capacitor.getPlatform() !== 'android') return;

  (async () => {
    try {
      const { PushNotifications } = await import('@capacitor/push-notifications');
      const { LocalNotifications } = await import('@capacitor/local-notifications');

      // Pre-create the high-importance trip-offers channel BEFORE register()
      // so any cold-start FCM payload referencing it can be displayed.
      try {
        await LocalNotifications.createChannel({
          id: 'trip-offers-v2',
          name: 'Trip Offers',
          description: 'Incoming trip offers',
          importance: 5,
          visibility: 1,
          vibration: true,
          lights: true,
        });
      } catch (err) {
        console.warn('[fcm-boot] createChannel failed', err);
      }

      try { await ensureAdminAlertsChannel(); } catch { /* ignore */ }

      try {
        await PushNotifications.addListener('registration', (token) => {
          primedToken = token.value;
          resolveWaiters(token.value);
        });
      } catch (err) {
        console.warn('[fcm-boot] registration listener failed', err);
      }

      try {
        await PushNotifications.addListener('registrationError', (err) => {
          console.error('[fcm-boot] registrationError', err);
          resolveWaiters(null);
        });
      } catch { /* ignore */ }

      // Permission is NOT required for token minting on Android — register()
      // mints a token even without POST_NOTIFICATIONS.
      try {
        await PushNotifications.register();
      } catch (err) {
        console.warn('[fcm-boot] register() failed', err);
        resolveWaiters(null);
      }
    } catch (err) {
      console.warn('[fcm-boot] init failed', err);
      resolveWaiters(null);
    }
  })();
}